<template>
    <FormSection title="Payer" type="his-section" :full-height="false" :application-id="referral.application">
        <template v-slot:content>
            <section class="row p-2" slot="content">
                <div class="col-12 col-lg-6 px-5 col-md-5 g-0">
                    <div class="form-group col-12">
                        <a
                            href=" https://www.cms.gov/Medicare/New-Medicare-Card/Providers/Providers-and-office-managers"
                            target="_blank"
                        >
                            Click here for CMS guidelines on the new Medicare Beneficiary Identifier (MBI)
                        </a>
                    </div>
                    <div class="form-group col-12">
                        MBI usage is required for all Medicare patients. Contact the patient or representative to obtain
                        the 11-digit MBI provided on the patient's Medicare card, and enter it in the patient's chart.
                        If the patient is unaware of his/her MBI, your
                        <a href="https://www.cms.gov/mac-info" target="_blank">
                            Medicare Administrative Contractor's provider portal
                        </a>
                        can be used to look up MBIs.
                    </div>
                    <div class="form-group col-12">
                        <label for="mbi-number" id="Mbi_Number_Label">
                            <span class="badge me-1">(M0063)</span> Medicare Beneficiary Identifier (MBI) Number
                            <span class="text-danger ms-1">*</span>
                        </label>
                        <input
                            class="form-control"
                            maxlength="11"
                            name="mbi-number"
                            type="text"
                            placeholder="Enter Medicare Beneficiary Identifier (MBI) Number"
                            :value="payorInfoValues.MbiNumber"
                            :disabled="payorInfoNotapplicable.MbiNumber && !insurerInfo.primary.isMedicare"
                            @change="payorInfoChanged(referral.Referral, 'MbiNumber')"
                            @input="setMbiNumber($event.target.value)"
                            @blur="v$.referral.Referral.MbiNumber.$touch"
                            :class="`${!v$.referral.Referral.MbiNumber.$error ? '' : 'field-invalid'}`"
                        />
                        <div class="form-group row">
                            <div class="col-12">
                                <div class="form-check custom-checkbox form-check-inline mt-2">
                                    <input
                                        type="checkbox"
                                        id="referral-medicare-beneficiary-identification-na"
                                        class="form-check-input au-target"
                                        v-model="payorInfoNotapplicable.MbiNumber"
                                        :disabled="insurerInfo.primary.isMedicare"
                                        @change="payorInfoChanged(referral.Referral, 'MbiNumber')"
                                    />
                                    <label
                                        class="form-check-label flex-grow-0"
                                        for="referral-medicare-beneficiary-identification-na"
                                    >
                                        <span class="me-1">N/A- Not Applicable</span>
                                    </label>
                                </div>
                            </div>
                        </div>
                        <span v-if="v$.referral.Referral.MbiNumber.$error">
                            <span
                                v-if="v$.referral.Referral.MbiNumber.$errors[0].$message == 'Value is required'"
                                class="field-invalid"
                                >MBI number is required</span
                            >
                            <span v-else class="field-invalid">Please enter 11 alpha-numeric characters.</span>
                        </span>
                    </div>
                    <div class="form-group col-12">
                        <label for="medicaid-number">
                            <span class="badge me-1">(M0065)</span> Medicaid Number
                            <span class="text-danger ms-1">*</span>
                        </label>
                        <input
                            class="form-control"
                            maxlength="50"
                            placeholder="Enter Medicaid Number"
                            name="medicaid-number"
                            type="text"
                            v-model="payorInfoValues.MedicaidNumber"
                            @change="payorInfoChanged(referral.Referral, 'MedicaidNumber')"
                            :disabled="payorInfoNotapplicable.MedicaidNumber"
                            @blur="v$.referral.Referral.MedicaidNumber.$touch"
                            :class="`${!v$.referral.Referral.MedicaidNumber.$error ? '' : 'field-invalid'}`"
                        />
                        <div class="form-group row">
                            <div class="col-12">
                                <div class="form-check custom-checkbox form-check-inline mt-2">
                                    <input
                                        type="checkbox"
                                        id="referral-no-medicaid"
                                        name="other-medicaid"
                                        class="form-check-input au-target"
                                        v-model="payorInfoNotapplicable.MedicaidNumber"
                                        @change="payorInfoChanged(referral.Referral, 'MedicaidNumber')"
                                    />
                                    <label class="form-check-label flex-grow-0" for="referral-no-medicaid">
                                        <span class="me-1">N/A- Not Applicable</span>
                                    </label>
                                </div>
                            </div>
                        </div>
                        <span v-if="v$.referral.Referral.MedicaidNumber.$error" class="field-invalid"
                            >Medicaid number is required.</span
                        >
                    </div>
                    <div class="form-group col-12">
                        <label for="medicaid-number"> Alternate Medicaid Number </label>
                        <input
                            class="form-control"
                            maxlength="50"
                            placeholder="Enter Alternate Medicaid Number"
                            name="medicaid-number-alternate"
                            type="text"
                            v-model="payorInfoValues.AlternateMedicaidNumber"
                            @input="payorInfoChanged(referral.ReferralExtension, 'AlternateMedicaidNumber')"
                            :disabled="payorInfoNotapplicable.AlternateMedicaidNumber"
                        />
                        <div class="form-group row">
                            <div class="col-12">
                                <div class="form-check custom-checkbox form-check-inline mt-2">
                                    <input
                                        type="checkbox"
                                        id="referral-no-medicaid-alternate"
                                        name="other-medicaid"
                                        class="form-check-input au-target"
                                        v-model="payorInfoNotapplicable.AlternateMedicaidNumber"
                                        @change="
                                            payorInfoChanged(referral.ReferralExtension, 'AlternateMedicaidNumber')
                                        "
                                    />
                                    <label class="form-check-label flex-grow-0" for="referral-no-medicaid-alternate">
                                        <span class="me-1">N/A- Not Applicable</span>
                                    </label>
                                </div>
                            </div>
                        </div>
                          <div class="mt-3">
                                    <button class="btn btn-xs btn-primary p-1" @click="verifyEligibility()">
                                        <span>Verify Medicare Eligibility</span>
                                    </button>
                                </div>
                    </div>
                </div>
                <div class="col-12 col-lg-6 ps-0 pe-5 col-md-5 g-0">
                    <div class="form-group col-12">
                        <label for="primary-insurance"> Primary Insurance </label>
                        <select
                            id="primary-insurance"
                            class="form-select"
                            @change="payorChanged(referral.Referral.PrimaryInsurance, 'primary')"
                            v-model="referral.Referral.PrimaryInsurance"
                        >
                            <option :value="null">Select Primary Insurance</option>
                            <option :value="null" disabled v-if="!referral.Referral.BranchId">
                                Please Select Agency Branch in the Demographics Tab
                            </option>
                            <template v-else>
                                <option
                                    v-for="(insurance, index) in primaryInsurance"
                                    :key="index"
                                    :value="insurance.value"
                                >
                                    {{ insurance.text }}
                                </option>
                            </template>
                        </select>
                    </div>
                    <MedicarePayorNew
                        :referral-options="referralOptions"
                        :referral="referral"
                        :insurer="insurerInfo.primary"
                        v-if="insurerInfo.primary?.isMedicare !== null && insurerInfo.primary?.isMedicare"
                    />

                    <NonMedicarePayorNew
                        :referral-options="referralOptions"
                        :referral="referral"
                        :insurer="insurerInfo.primary"
                        :clearAddressfields="clearAddressfields"
                        v-if="insurerInfo.primary?.isMedicare !== null && !insurerInfo.primary?.isMedicare"
                    />
                    <template v-if="secondaryInsurance.length">
                        <div class="form-group col-12">
                            <label for="secondary-insurance"> Secondary Insurance </label>
                            <select
                                id="secondary-insurance"
                                class="form-select"
                                @change="payorChanged(referral.Referral.SecondaryInsurance, 'secondary')"
                                v-model="referral.Referral.SecondaryInsurance"
                            >
                                <option :value="null">Select Secondary Insurance</option>
                                <option :value="null" disabled v-if="!referral.Referral.BranchId">
                                    Please Select Agency Branch in the Demographics Tab
                                </option>
                                <template v-else>
                                    <option
                                        v-for="(insurance, index) in secondaryInsurance"
                                        :key="index"
                                        :value="insurance.value"
                                    >
                                        {{ insurance.text }}
                                    </option>
                                </template>
                            </select>
                        </div>
                        <MedicarePayorNew
                            :referral-options="referralOptions"
                            :referral="referral"
                            :insurer="insurerInfo.secondary"
                            v-if="insurerInfo.secondary.isMedicare !== null && insurerInfo.secondary?.isMedicare"
                        />

                        <NonMedicarePayorNew
                            :insurer="insurerInfo.secondary"
                            :referral-options="referralOptions"
                            :referral="referral"
                            :clearAddressfields="clearAddressfields"
                            v-if="insurerInfo.secondary?.isMedicare !== null && !insurerInfo.secondary?.isMedicare"
                        />
                    </template>
                    <template v-if="tertiaryInsurance.length">
                        <div class="form-group col-12">
                            <label for="tertiary-insurance"> Tertiary Insurance </label>
                            <select
                                id="tertiary-insurance"
                                class="form-select"
                                @change="payorChanged(referral.Referral.TertiaryInsurance, 'tertiary')"
                                v-model="referral.Referral.TertiaryInsurance"
                            >
                                <option :value="null">Select Tertiary Insurance</option>
                                <option :value="null" disabled v-if="!referral.Referral.BranchId">
                                    Please Select Agency Branch in the Demographics Tab
                                </option>
                                <template v-else>
                                    <option
                                        v-for="(insurance, index) in tertiaryInsurance"
                                        :key="index"
                                        :value="insurance.value"
                                    >
                                        {{ insurance.text }}
                                    </option>
                                </template>
                            </select>
                        </div>
                        <MedicarePayorNew
                            :referral-options="referralOptions"
                            :referral="referral"
                            :insurer="insurerInfo.tertiary"
                            v-if="insurerInfo.tertiary?.isMedicare !== null && insurerInfo.tertiary?.isMedicare"
                        />

                        <NonMedicarePayorNew
                            :insurer="insurerInfo.tertiary"
                            :referral-options="referralOptions"
                            :referral="referral"
                            :clearAddressfields="clearAddressfields"
                            v-if="insurerInfo.tertiary?.isMedicare !== null && !insurerInfo.tertiary?.isMedicare"
                        />
                    </template>
                </div>
            </section>
        </template>
    </FormSection>
</template>

<script lang="ts" src="./PayorInformation.ts"></script>
